import { TrainIdentifier } from "@vygruppen/vy-train-map";
import { trainIdentifierIsEqual } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/utils";
import { AffectedTrain } from "features/CenterContent/RoleContent/Vaktleder/types";
import {
  TrainFormGroup,
  TrickFormHookGroupedTrainFormSchema,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { TrainSeriesGrouping } from "features/History/groupTrainSeries";
import { LogLevel, log } from "logging/datadogBrowserLogs";
import { FC } from "react";
import {
  UseFormGetValues,
  UseFormSetValue,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ChoiceChip,
  ChoiceChipGroup,
  HStack,
  Stack,
  Text,
} from "shared/components/Spor";
import { DropsStepper } from "shared/components/stepper/DropsStepper";
import { InfrastructureIncidentIcon18 } from "shared/icons/InfrastructureIncidentIcon18";

type TrainInfoBatchModalHeaderProps = {
  activeTrainSeriesKey: `\${groupKey}`;
  activeTrainSeriesIndex: number;
  setActiveTrainSeriesIndex: (index: number) => void;
  activeTrainSeries: AffectedTrain[];
  activeTrainGrouping: TrainSeriesGrouping;
};

type SelectedTrainProps = {
  selectedTrains: AffectedTrain[];
  updateSelectedTrains: (selectedTrain: AffectedTrain) => void;
};

function removeTrainFromGroup(
  getValues: UseFormGetValues<TrickFormHookGroupedTrainFormSchema>,
  setValue: UseFormSetValue<TrickFormHookGroupedTrainFormSchema>,
  // reset: UseFormReset<TrickFormHookGroupedTrainFormSchema>,
  trainId: TrainIdentifier,
  trainSeriesKey: `\${groupKey}`,
): void {
  const trainGroups: TrainFormGroup[] = getValues(trainSeriesKey);

  // Get the index of the group that contains the current train
  const relevantTrainGroupIndex = trainGroups.findIndex(
    (trainGroup) =>
      trainGroup.trainIdsInGroup.find((id) =>
        trainIdentifierIsEqual(trainId, id),
      ) !== undefined,
  );

  if (relevantTrainGroupIndex === -1) {
    // Train isn't in any of these groups
    // Should not happen
    log(
      LogLevel.error,
      "TrainInfoBatchModalHeader/removeTrainFromGroup",
      `Train ${trainId.identifier} is not in any group in series ${trainSeriesKey}: invalid batch state`,
    );
    return;
  }

  const relevantTrainGroup = trainGroups[relevantTrainGroupIndex];

  if (
    relevantTrainGroup.trainIdsInGroup.length === 1 &&
    trainGroups.length === 1
  ) {
    // Train series will also be empty after removal, delete the entire series
    // This is not allowed for now, do nothing
    // const trainForm = getValues(); // The whole form;
    // Filter out the current train series
    /* const nextForm = Object.entries(trainForm).filter(
        ([key]) => key !== trainSeriesKey,
      );

      reset(Object.fromEntries(nextForm));
      */
  } else {
    // Series includes other trains, just remove the trainId from the state
    // Remove the train from the group, but keep the group in state
    const nextGroups = relevantTrainGroup.trainIdsInGroup.filter(
      (id) => !trainIdentifierIsEqual(id, trainId),
    );

    setValue(
      `${trainSeriesKey}.${relevantTrainGroupIndex}.trainIdsInGroup`,
      nextGroups,
    );
  }
}

const TrainInfoBatchModalHeader: FC<
  TrainInfoBatchModalHeaderProps & SelectedTrainProps
> = ({
  activeTrainSeriesKey,
  activeTrainSeriesIndex,
  setActiveTrainSeriesIndex,
  activeTrainSeries,
  activeTrainGrouping,
  selectedTrains,
  updateSelectedTrains,
}) => {
  const { t } = useTranslation();
  const { setValue, getValues } =
    useFormContext<TrickFormHookGroupedTrainFormSchema>();

  const trainSeriesInActiveGroup = Object.keys(activeTrainGrouping.trainSeries);

  return (
    <Stack gap={4} overflowX="hidden">
      <HStack>
        <InfrastructureIncidentIcon18 />
        <Text>{`${t("Infrastrukturhendelse")}: ${activeTrainGrouping.stretchName}`}</Text>
      </HStack>

      <Stack gap={2}>
        <Stack alignItems="flex-start" overflowX="auto">
          <DropsStepper
            variant="core"
            onClick={(step) => setActiveTrainSeriesIndex(step)}
            activeStep={activeTrainSeriesIndex}
            steps={trainSeriesInActiveGroup}
          />
        </Stack>
        <Stack gap={1}>
          <Text fontWeight="bold">{t("Valgte tog i serien:")}</Text>
          <HStack wrap="wrap">
            {selectedTrains.length === 1 ? (
              <ChoiceChipGroup
                size="sm"
                defaultValue={selectedTrains[0].trainId.identifier}
              >
                <ChoiceChip value={selectedTrains[0].trainId.identifier}>
                  {selectedTrains[0].trainId.identifier}
                </ChoiceChip>
              </ChoiceChipGroup>
            ) : (
              activeTrainSeries.map((train, index) => {
                const chipValue = `${train.trainId.identifier}_${train.trainId.nominalDate}_${train.incidentId ?? index}`;
                return (
                  <ChoiceChipGroup
                    size="sm"
                    key={chipValue}
                    defaultValue={chipValue}
                  >
                    <ChoiceChip
                      value={chipValue}
                      onClick={() => {
                        // Remove the trains from the form state
                        removeTrainFromGroup(
                          getValues,
                          setValue,
                          // reset,
                          train.trainId,
                          activeTrainSeriesKey,
                        );
                        // Also remove the trains from the selected trains list in affectedTrains
                        updateSelectedTrains(train);
                      }}
                    >
                      {train.trainId.identifier}
                    </ChoiceChip>
                  </ChoiceChipGroup>
                );
              })
            )}
          </HStack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export { TrainInfoBatchModalHeader };
export type { SelectedTrainProps };
